import { useState, useEffect, useContext, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import "./RegisterUserPage.css";
import { Box, InputAdornment, TextField } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import InputMask from "react-input-mask";
import { AuthContext } from "../../state/AuthContext";
import { getFirstName, getLastName } from "../../utils/fieldAccessors";
import {
  isChromeBrowser,
  browserFamily,
  isAppleTouchDevice,
  isMacDesktop,
  devicePlatform,
  deviceFormFactor,
  isEmbeddedWebView,
} from "../../utils/isChromeBrowser";
import { launchExternalUrl } from "../../utils/launchExternalUrl";
import {
  addBreadcrumb,
  captureError,
  captureMessage,
} from "../../config/sentry";

// #588. Both wrong answers strand a real caregiver, in opposite ways:
// "your invite is dead, get a new one" throws away a link that still
// works, and "try again shortly" leaves someone whose invite really is
// dead retrying forever instead of asking for a replacement — which is
// the original dead-end this ticket exists to remove.
//
// So we only speak with confidence where the status earns it, and an
// unrecognised 4xx gets copy offering BOTH recoveries rather than a guess.
const TERMINAL_INVITE_STATUSES = new Set([404, 410]);
const TRANSIENT_INVITE_STATUSES = new Set([408, 425, 429]);

// The invite code is the registration credential. It must never reach
// telemetry, a log, or an analytics payload — a breadcrumb carrying the
// full request URL carries the code with it.
function redactInviteCode(rawUrl) {
  if (typeof rawUrl !== "string") return "";
  const q = rawUrl.indexOf("?");
  return q === -1 ? rawUrl : `${rawUrl.slice(0, q)}?invite_code=[redacted]`;
}

// addBreadcrumb() only attaches to the local Sentry scope — nothing is
// transmitted until an event is captured. Both #588 and #590 specify a
// confirming FILTER, so a breadcrumb alone would have shipped the
// appearance of observability: the filters would return nothing and we
// would read that silence as "no incidents" rather than "no telemetry".
// Deduped per MOUNT, not per module: a module-level guard would live for
// the whole SPA session, so a caregiver who hit the gate, navigated away
// and came back would emit nothing the second time — and the volume this
// guards against is re-renders within one visit, not repeat visits.

// The breadcrumb vocabulary #588's confirming filter documents. A fourth
// category would be invisible to the query the incident is monitored by,
// so the finer outcome travels in the payload instead of the category.
function inviteBreadcrumbCategory(outcome) {
  return outcome === "invalid"
    ? "register.invite.invalid"
    : "register.invite.unavailable";
}

function classifyInviteFailure(status) {
  if (TERMINAL_INVITE_STATUSES.has(status)) return "invalid";
  if (TRANSIENT_INVITE_STATUSES.has(status)) return "busy";
  if (status >= 400 && status < 500) return "unverified";
  return "unavailable";
}

// GMA-1177: the invite fetch must never raise an unhandled TypeError.
// If the base URL is missing or malformed the only safe response is to
// send the user to /login so the session does not half-load.
function buildInviteUrl(inviteCode) {
  const base = process.env.REACT_APP_BASE_URL;
  if (!base || typeof base !== "string") return null;
  try {
    const v2Base = base.replace("v1", "v2");
    // Validate URL parses; throws on malformed values.
    new URL(v2Base);
    return `${v2Base}/users/invite_code?invite_code=${encodeURIComponent(inviteCode)}`;
  } catch (_err) {
    return null;
  }
}

const RegisterUserPage = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [title, setTitle] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const [message, _setMessage] = useState("");
  const [searchParams, _setSearchParams] = useSearchParams();
  const [isLoaded, setIsLoaded] = useState(false);
  // #588: one of four outcomes from classifyInviteFailure() —
  //   "invalid"     404/410      the link is finished, get a new one
  //   "busy"        408/425/429  the lookup did not complete; the
  //                              link's status is unknown
  //   "unverified"  other 4xx    we cannot tell; offer both recoveries
  //   "unavailable" 5xx          our side is broken
  // Each renders on-page guidance instead of the old silent /login bounce,
  // which left a stuck caregiver on a login screen for an account that was
  // never finished, with no explanation.
  const [inviteError, setInviteError] = useState(null);
  // #590: registration requires real Chrome. Computed once — the browser
  // cannot change mid-mount, and a state initializer keeps the render and
  // the effect reading the same verdict.
  const [chromeOk] = useState(() => isChromeBrowser());
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
  const navigate = useNavigate();
  const [passwordError, setPasswordError] = useState("");
  const { logout } = useContext(AuthContext);
  const abortRef = useRef(null);
  const gateRef = useRef(null);
  const emittedEventsRef = useRef(new Set());

  // How the caregiver opened the invite — searchable on every register
  // event so the next incident report reads from Sentry (phone vs tablet vs
  // desktop, and whether an email app's embedded browser was involved)
  // instead of being reconstructed from server logs.
  const deviceTags = () => ({
    browser: browserFamily(),
    platform: devicePlatform(),
    form_factor: deviceFormFactor(),
    webview: String(isEmbeddedWebView()),
  });

  const emitRegisterEvent = (name, tags, level = "warning") => {
    if (emittedEventsRef.current.has(name)) return;
    emittedEventsRef.current.add(name);
    captureMessage(name, { level, tags: { ...deviceTags(), ...tags } });
  };

  // #590 ("keep the interstitial accessible — roles, focus"). /register is
  // reached by SPA navigation, which swaps the DOM without moving focus: a
  // keyboard or screen-reader user is left on the previous route's content
  // and is never told the form was replaced by a requirement.
  useEffect(() => {
    if (!chromeOk && gateRef.current) {
      gateRef.current.focus();
    }
  }, [chromeOk]);

  useEffect(() => {
    if (isLoaded) {
      return undefined;
    }

    // #590: gated browsers get the interstitial only — no logout, no invite
    // fetch. The page must be inert so nothing about the invite changes
    // until the caregiver is in Chrome.
    if (!chromeOk) {
      addBreadcrumb("register.browser_gate", {
        category: "register.browser_gate",
        level: "info",
        browser: browserFamily(),
        userAgent:
          typeof navigator !== "undefined" ? navigator.userAgent : "none",
      });
      emitRegisterEvent("register.browser_gate", { route: "register" });
      return undefined;
    }

    logout();

    const invite_code = searchParams.get("invite_code");

    if (!invite_code) {
      navigate("/login");
      return undefined;
    }

    abortRef.current = new AbortController();
    fetchUser(invite_code, abortRef.current.signal);

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [isLoaded]);

  // GMA-1177: guarded invite fetch — it must never raise an unhandled
  // TypeError: Failed to fetch. A network failure, a missing or malformed
  // env var, or an unreadable body still redirects to /login.
  //
  // A non-OK response no longer does (#588). That redirect WAS the
  // caregiver dead-end: an expired or already-used invite bounced to a
  // login screen for an account that was never finished, with no message.
  // Non-OK is now classified before the body is touched and rendered as
  // on-page guidance. Do not restore the redirect here.
  async function fetchUser(invite_code, signal) {
    const url = buildInviteUrl(invite_code);
    if (!url) {
      console.error("Invalid REACT_APP_BASE_URL; redirecting to /login");
      toast.error("App configuration error. Please request a new invitation link.");
      navigate("/login");
      return;
    }

    addBreadcrumb("register.fetchInvite", {
      category: "register",
      level: "info",
      // NEVER the invite code: it is the credential for this registration
      // — anyone holding it can complete the account. Only the endpoint
      // shape is useful for debugging, so the query string is dropped.
      url: redactInviteCode(url),
    });

    let response;
    try {
      // GET request: no body, no Content-Type -> avoids an extra CORS preflight
      // and matches how well-behaved link scanners hit the endpoint.
      response = await fetch(url, { signal });
    } catch (err) {
      if (err && err.name === "AbortError") {
        return; // component unmounted or navigated away; nothing to do
      }
      captureError(err, {
        // Device tags on the failure path too (review finding): the fetch
        // error is exactly the event the next incident will be read from.
        tags: { ...deviceTags(), route: "register", step: "fetchInvite" },
      });
      toast.error("Could not load your invitation. Please try the link again or request a new one.");
      navigate("/login");
      return;
    }

    // #588 (review finding): classify the status BEFORE touching the body.
    // Error responses from gateways/proxies are often empty or HTML, so
    // response.json() on a non-OK response throws — and the parse-error
    // branch would redirect to /login, recreating the silent bounce.
    if (!response.ok) {
      const kind = classifyInviteFailure(response.status);
      const category = inviteBreadcrumbCategory(kind);
      addBreadcrumb(category, {
        category,
        level: "warning",
        status: response.status,
        // The four-way outcome, queryable without adding a category the
        // documented filter would miss.
        outcome: kind,
      });
      emitRegisterEvent(category, {
        route: "register",
        outcome: kind,
        invite_status: String(response.status),
      });
      setInviteError(kind);
      return;
    }

    let data = null;
    try {
      data = await response.json();
    } catch (err) {
      if (err && err.name === "AbortError") return;
      captureError(err, {
        tags: { ...deviceTags(), route: "register", step: "parseInvite" },
      });
      toast.error("Could not read invitation response. Please try again.");
      navigate("/login");
      return;
    }

    const firstName = getFirstName(data, "");
    const lastName = getLastName(data, "");

    setFirstName(firstName);
    setLastName(lastName);
    setName(`${firstName} ${lastName}`.trim() || "User");
    setEmail(data.email || "");
    setUserType(data.userType || "");
    setCity(data.city || "");
    setState(data.state || "");
    setCompany(data.company || "");
    setTitle(data.title || "");
    setPhone(data.phone || "");

    const createdById = data.createdBy ? parseInt(data.createdBy) : 0;

    if (!createdById || isNaN(createdById) || createdById <= 0) {
      console.error("Invalid createdBy value:", data.createdBy);
      toast.error("Invalid invitation: Missing creator information. Please request a new invitation.");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }

    setCreatedBy(createdById);
    // The success half of #588's telemetry, emitted only once the payload
    // has actually been ACCEPTED (review finding: emitting before the
    // createdBy validation counted a 200-then-rejected invitation as a
    // success, corrupting the loaded-vs-invalid ratio the incident report
    // reads). It is also the record of HOW each caregiver accepted the
    // invite (device tags).
    addBreadcrumb("register.invite.loaded", {
      category: "register.invite.loaded",
      level: "info",
    });
    emitRegisterEvent("register.invite.loaded", { route: "register" }, "info");
    setIsLoaded(true);
    setPasswordError("");
  }

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);

  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);
  const handleMouseDownConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error("Passwords do not match. Please check and try again.");
      setPassword("");
      setConfirmPassword("");
      return;
    }

    try {
      const invite_code = searchParams.get("invite_code");

      // Validate created_by is a valid positive integer
      const createdById = createdBy ? parseInt(createdBy) : 0;
      
      if (!createdById || isNaN(createdById) || createdById <= 0) {
        console.error('Invalid createdBy value during submit:', createdBy);
        toast.error("Invalid invitation. Please request a new invitation link.");
        return;
      }

      const params = {
        invite_code,
        email,
        password,
        name,
        first_name: firstName,
        last_name: lastName,
        title,
        company,
        city,
        state,
        phone,
        created_by: createdById,
        user_type: userType,
      };

      if (!invite_code) {
        toast.error("Invalid invite code. Please try again.");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL.replace("v1", "v2")}/users/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params),
        },
      );

      const data = await response.json();
      if (response.ok) {
        logout();
        
        // Show success message
        const isAutoConfirm = process.env.REACT_APP_FEATURE_FLAG_USERS_AUTO_CONFIRM === "true";
        if (isAutoConfirm) {
          toast.success("Registration successful! You can now log in with your credentials.");
        } else {
          toast.success("Registration successful! Check your email for a verification code.");
        }
        
        // Clear form
        setName("");
        setEmail("");
        setTitle("");
        setCompany("");
        setCity("");
        setState("");
        setPassword("");
        setConfirmPassword("");
        
        // Navigate after a brief delay to let user see the message
        setTimeout(() => {
          navigate(
            isAutoConfirm ? "/login" : "/users/confirm_account",
            { replace: true }
          );
        }, 2000);
      } else {
        setPasswordError(data.message);
        toast.error(data.message || "Registration failed. Please try again.");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error(`Error during registration: ${error.message}. Please try again.`);
    }
  };

  function toTitleCase(str) {
    return str
      .replace(/_/g, " ")
      .replace(
        /\w\S*/g,
        (text) =>
          text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
      );
  }

  if (!chromeOk) {
    // Rebuild the canonical invite URL from the route itself (origin +
    // /register + invite_code) rather than echoing window.location.href —
    // deterministic under the test router, and strips any stray params an
    // email client appended.
    const gateInviteCode = searchParams.get("invite_code") || "";
    const inviteHref =
      typeof window !== "undefined" && window.location
        ? `${window.location.origin}/register?invite_code=${encodeURIComponent(gateInviteCode)}`
        : "";
    // iOS/iPadOS Chrome registers the googlechrome(s):// scheme, which DOES
    // carry the URL. isAppleTouchDevice() rather than a UA test: an iPadOS
    // 13+ iPad sends a Macintosh UA and would otherwise be gated with no
    // way out.
    const openInChromeHref = isAppleTouchDevice()
      ? inviteHref.replace(/^https:/, "googlechromes:").replace(/^http:/, "googlechrome:")
      : null;
    // macOS Chrome registers only the bare `google-chrome:` LAUNCH scheme
    // (its Info.plist "Direct launch URL"; measured — a URL payload is not
    // carried). So the mac recovery is one click that copies the invite
    // link AND brings Chrome up; the caregiver pastes. Safari asks "allow
    // this page to open Google Chrome?" — expected, not an error.
    const chromeLaunchHref = isMacDesktop() ? "google-chrome://" : null;
    const handleCopyInviteLink = async () => {
      try {
        if (navigator?.clipboard?.writeText && inviteHref) {
          await navigator.clipboard.writeText(inviteHref);
          setInviteLinkCopied(true);
          return true;
        }
      } catch (_err) {
        // Clipboard can be blocked; the link is rendered inline below as a
        // manual-copy fallback.
      }
      return false;
    };
    // Review finding: the combined action must not launch Chrome when the
    // copy did not actually happen — Chrome opening beside a stale
    // clipboard breaks exactly the promise the label makes, and the manual
    // fallback is left behind in Safari. Launch only after a CONFIRMED
    // copy; otherwise stay here, where the copy button and the inline link
    // remain visible.
    const handleCopyAndOpenChrome = async (event) => {
      event.preventDefault();
      const copied = await handleCopyInviteLink();
      if (copied && chromeLaunchHref) {
        launchExternalUrl(chromeLaunchHref);
      }
    };
    return (
      <div className="modal-overlay">
        <div className="modal" style={{ display: "inline-block" }}>
          <div
            role="region"
            aria-label="Google Chrome required"
            ref={gateRef}
            tabIndex={-1}
          >
            <h1>Please use Google Chrome to register</h1>
            <p>
              CompassCare registration works in Google Chrome. The browser you
              are using now is not supported for this step.
            </p>
            <ol style={{ textAlign: "left" }}>
              <li>
                If you do not have Chrome yet:{" "}
                <a
                  href="https://www.google.com/chrome/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Download Google Chrome
                </a>
                .
              </li>
              <li>
                <button type="button" onClick={handleCopyInviteLink}>
                  Copy invitation link
                </button>{" "}
                {inviteLinkCopied && (
                  <span role="status">Copied — now open Chrome.</span>
                )}
              </li>
              {chromeLaunchHref && (
                <li>
                  <a href={chromeLaunchHref} onClick={handleCopyAndOpenChrome}>
                    Copy link and open Google Chrome
                  </a>{" "}
                  — then paste the link into Chrome&apos;s address bar. (If
                  Chrome is installed, your browser will ask permission to
                  open it.)
                </li>
              )}
              <li>Open Chrome and paste the link into the address bar.</li>
            </ol>
            {openInChromeHref && (
              <p>
                <a href={openInChromeHref}>Open this page in Chrome</a>
              </p>
            )}
            <p style={{ fontSize: "0.9rem", wordBreak: "break-all" }}>
              Your invitation link: <code>{inviteHref}</code>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (inviteError) {
    return (
      <div className="modal-overlay">
        <div className="modal" style={{ display: "inline-block" }}>
          <div role="alert">
            {inviteError === "invalid" ? (
              <>
                <h1>This invitation link is no longer valid</h1>
                <p>
                  It may have already been used, or a newer invitation may have
                  replaced it. Please ask your care administrator to send you a
                  new invitation.
                </p>
                <p>
                  If you already created your password and were asked for a
                  6-digit code, you can{" "}
                  <Link to="/users/confirm_account">
                    finish setting up your account
                  </Link>
                  .
                </p>
                <p>
                  Already registered? <Link to="/login">Sign in</Link>.
                </p>
              </>
            ) : inviteError === "busy" ? (
              <>
                <h1>We couldn&apos;t check your invitation just now</h1>
                {/* No resend suggestion on a KNOWN-transient status: the
                    LOOKUP did not complete, which leaves the link's status
                    unknown — so sending the caregiver to an administrator
                    now would risk discarding a good invite. The copy
                    asserts nothing about the link in either direction; a
                    timeout cannot establish validity or invalidity. */}
                <p>
                  We could not reach our system to check your invitation.
                  Please try your invitation link again in a few moments.
                </p>
              </>
            ) : inviteError === "unverified" ? (
              <>
                <h1>We couldn&apos;t check your invitation</h1>
                {/* An unrecognised 4xx: we genuinely do not know whether
                    the link is dead or the request simply failed, so this
                    offers BOTH recoveries rather than guessing one. A
                    wrong guess strands the caregiver either way — retrying
                    a dead invite forever, or discarding a working one. */}
                <p>
                  Something went wrong checking your invitation. Please try
                  your invitation link again in a few moments — and if it
                  keeps happening, ask your care administrator to send you a
                  new invitation.
                </p>
                <p>
                  If you already created your password and were asked for a
                  6-digit code, you can{" "}
                  <Link to="/users/confirm_account">
                    finish setting up your account
                  </Link>
                  .
                </p>
              </>
            ) : (
              <>
                <h1>We couldn&apos;t load your invitation</h1>
                <p>
                  Something went wrong on our side. Please try your invitation
                  link again in a few minutes, or ask your care administrator
                  to resend it.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return !isLoaded ? (
    <div></div>
  ) : (
    <div className="modal-overlay">
      <div className="modal" style={{ display: "inline-block" }}>
        <h1>Register as {toTitleCase(userType)} </h1>
        <form onSubmit={handleSubmit}>
          <div className="input-container">
            <label htmlFor="email">Email:</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled
              required
            />
          </div>
          <div className="input-container">
            <label htmlFor="firstName">First Name:</label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>
          <div className="input-container">
            <label htmlFor="lastName">Last Name:</label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>

          <div className="input-container">
            <label htmlFor="state">Phone:</label>
            <InputMask
              mask="+19999999999"
              value={phone}
              maskChar=" "
              onChange={(e) => setPhone(e.target.value)}
            >
              {(inputProps) => (
                <input
                  {...inputProps}
                  type="text"
                  id="phone"
                  value={phone}
                  required
                />
              )}
            </InputMask>
          </div>
          <Box
            sx={{ display: "flex", flexWrap: "wrap" }}
            style={{ marginTop: "24px" }}
          >
            <label htmlFor="password">Password:</label>
            <TextField
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </InputAdornment>
                ),
              }}
              style={{ width: "70%", marginLeft: "auto" }}
            />
            {passwordError && (
              <p
                style={{ color: "red", fontSize: "0.875rem", marginTop: "4px" }}
              >
                {passwordError}
              </p>
            )}
          </Box>
          <Box
            sx={{ display: "flex", flexWrap: "wrap" }}
            style={{ marginTop: "30px" }}
          >
            <label htmlFor="confirmPassword">Confirm Password:</label>
            <TextField
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position="end"
                    onClick={handleClickShowConfirmPassword}
                    onMouseDown={handleMouseDownConfirmPassword}
                  >
                    {showConfirmPassword ? (
                      <VisibilityOffIcon />
                    ) : (
                      <VisibilityIcon />
                    )}
                  </InputAdornment>
                ),
              }}
              style={{ width: "70%", marginLeft: "auto" }}
            />
          </Box>
          <button type="submit" className="addStepButton">
            Register
          </button>
        </form>
        {message && <p>{message}</p>}
      </div>
    </div>
  );
};

export default RegisterUserPage;
